import { z } from 'zod'
import { fromZodError } from 'zod-validation-error'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import { client } from '../../services/client'
import { QueryContextFromKeys } from '../../utils/react-query'
import {
  makePaginatedServerResponseSchema,
  makeServerResponseSchema,
} from '../../utils/schema'
import {
  clientImportDetailBaseSchema,
  clientImportSchema,
} from './client-import.schema'

type ClientImportQueryContext = QueryContextFromKeys<
  typeof clientImportQueryKeys
>

const clientImportQueryKeys = {
  all: [{ entyity: 'clientImport' }] as const,
  clientImportId: (clientImportId: string) =>
    [{ ...clientImportQueryKeys.all[0], clientImportId }] as const,
}

async function fetchClientImport({
  queryKey: [{ clientImportId }],
}: ClientImportQueryContext['clientImportId']) {
  const response = await client
    .post('functions/getClientImport', {
      json: { objectId: clientImportId },
    })
    .json()

  const parsedResult = makeServerResponseSchema(
    clientImportDetailBaseSchema
  ).safeParse(response)

  if (!parsedResult.success) throw new Error(parsedResult.error.message)

  return parsedResult.data.result
}

function useClientImport(id: string) {
  return useQuery({
    queryKey: clientImportQueryKeys.clientImportId(id),
    queryFn: fetchClientImport,
  })
}

async function fetchClientImports({
  pageParam = 0,
}: ClientImportQueryContext['all']) {
  const response = await client
    .post('functions/getClientImports', {
      json: {
        page: pageParam,
      },
    })
    .json()

  const parsedResponse = makePaginatedServerResponseSchema(
    z.array(clientImportSchema)
  ).safeParse(response)

  if (!parsedResponse.success)
    throw new Error(fromZodError(parsedResponse.error).message)

  return parsedResponse.data.result
}

function useClientImports() {
  return useInfiniteQuery({
    queryKey: clientImportQueryKeys.all,
    queryFn: fetchClientImports,
    getNextPageParam: (lastPage) => lastPage.hasNextPage && lastPage.page + 1,
  })
}

export { useClientImport, useClientImports, clientImportQueryKeys }
